/**
 * GlassCard Component
 *
 * Frosted glass cards for content containers (input fields, results, modals).
 *
 * Features:
 * - Optional chrome metallic border (silver gradient)
 * - Configurable blur intensity (default 40)
 * - Solid white background (85% opacity)
 * - Elevation shadow for depth
 * - WCAG AA: Black text on rgba(255,255,255,0.85) = 17:1 contrast (AAA)
 *
 * @example
 * // With chrome border (default)
 * <GlassCard>
 *   <MessageInput />
 * </GlassCard>
 *
 * // Custom intensity, no border
 * <GlassCard intensity={60} withChromeBorder={false}>
 *   <AnalysisResults />
 * </GlassCard>
 */

import React, { ReactNode } from 'react';
import { View, StyleSheet, StyleProp, ViewStyle, Platform } from 'react-native';
import { BlurView } from 'expo-blur';
import { LinearGradient } from 'expo-linear-gradient';
import { createBoxShadow } from '../../lib/design-tokens';

// Chrome metallic gradient (silver)
const CHROME_GRADIENT = ['#E0E0E0', '#F5F5F5', '#C0C0C0', '#F5F5F5'] as const;

interface GlassCardProps {
  children: ReactNode;
  /** Blur intensity (10-100). Default: 40. Higher = more frosted. */
  intensity?: number;
  /** Add chrome metallic border. Default: true. */
  withChromeBorder?: boolean;
  /** Remove default 16px inner padding. Useful when children handle their own padding. */
  noPadding?: boolean;
  /** Additional style for container */
  style?: StyleProp<ViewStyle>;
}

export const GlassCard: React.FC<GlassCardProps> = ({
  children,
  intensity = 40,
  withChromeBorder = true,
  noPadding = false,
  style,
}) => {
  // Web implementation using CSS backdrop-filter
  if (Platform.OS === 'web') {
    const webBlurAmount = Math.round(intensity * 0.3); // Convert 40 → 12px blur

    const webGlassContent = (
      <View
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: `blur(${webBlurAmount}px)`,
          WebkitBackdropFilter: `blur(${webBlurAmount}px)`,
          borderRadius: withChromeBorder ? 14 : 16,
          padding: noPadding ? 0 : 16,
        } as any}
      >
        {children}
      </View>
    );

    if (!withChromeBorder) {
      return (
        <View style={[styles.container, style]}>
          {webGlassContent}
        </View>
      );
    }

    // With chrome metallic border (CSS gradient)
    return (
      <View style={[styles.container, style]}>
        <View
          style={{
            background: `linear-gradient(135deg, ${CHROME_GRADIENT[0]} 0%, ${CHROME_GRADIENT[1]} 33%, ${CHROME_GRADIENT[2]} 66%, ${CHROME_GRADIENT[3]} 100%)`,
            padding: 2,
            borderRadius: 16,
          } as any}
        >
          {webGlassContent}
        </View>
      </View>
    );
  }

  // Mobile implementation
  // Android: Use static RGBA fallback for performance (blur is expensive in virtualized lists)
  // iOS: Use expo-blur for authentic glassmorphism
  const contentStyle = noPadding ? styles.contentNoPadding : styles.content;
  const glassContent = Platform.OS === 'android' ? (
    <View
      style={[
        styles.glass,
        { backgroundColor: 'rgba(255, 255, 255, 0.85)' }, // Solid white for Android
      ]}
    >
      <View style={contentStyle}>{children}</View>
    </View>
  ) : (
    <BlurView intensity={intensity} tint="light" style={styles.glass}>
      <View style={contentStyle}>{children}</View>
    </BlurView>
  );

  if (!withChromeBorder) {
    return (
      <View style={[styles.container, style]}>
        {glassContent}
      </View>
    );
  }

  // With chrome metallic border (2px gradient)
  return (
    <View style={[styles.container, style]}>
      <LinearGradient
        colors={CHROME_GRADIENT}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.chromeBorder}
      >
        {glassContent}
      </LinearGradient>
    </View>
  );
};

// Platform-aware shadow style for container
const containerShadow = createBoxShadow({
  color: '#000',
  offsetX: 0,
  offsetY: 4,
  blurRadius: 12,
  opacity: 0.15,
  elevation: 6,
});

const styles = StyleSheet.create({
  container: {
    borderRadius: 16,
    overflow: 'hidden',
    ...containerShadow,
  } as ViewStyle,
  chromeBorder: {
    padding: 2, // 2px chrome border
    borderRadius: 16,
  },
  glass: {
    backgroundColor: 'rgba(255, 255, 255, 0.85)', // 85% white (solid card)
    borderRadius: 14, // Inner radius (16 - 2px border)
  },
  content: {
    padding: 16,
  },
  contentNoPadding: {
    padding: 0,
  },
});
